import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ContactWidget from '../../components/ui/ContactWidget';
import Breadcrumb from '../../components/ui/Breadcrumb';
import HeroSection from './components/HeroSection';
import ServicesOverview from './components/ServicesOverview';
import CompanyIntroduction from './components/CompanyIntroduction';
import RelevantTopics from './components/RelevantTopics';
import TestimonialsCarousel from './components/TestimonialsCarousel';
import NewsletterSubscription from './components/NewsletterSubscription';

const Homepage = () => {

  // --- COMPREHENSIVE SEO STRUCTURED DATA (JSON-LD) ---
  // This data gives Google detailed, machine-readable information about your business,
  // significantly boosting local SEO and overall search presence.
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["Organization", "LocalBusiness"],
        "@id": "https://www.yourwebsite.com/#organization", // IMPORTANT: Replace with your domain
        "name": "Luxe & Allure Events Company",
        "url": "https://www.yourwebsite.com/", // IMPORTANT: Replace with your domain
        "logo": "https://www.yourwebsite.com/assets/logoo.png", // IMPORTANT: Full URL to your logo
        "telephone": "+254727937010",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Nairobi",
          "addressCountry": "KE"
        },
        "areaServed": {
          "@type": "Country",
          "name": "Kenya"
        },
        "sameAs": [ // Links your website to your social media profiles for Google
          "https://www.facebook.com/luxeallureevents/",
          "https://www.instagram.com/luxeallureevents/",
          "https://www.tiktok.com/discover/luxe-allure-events?lang=en"
        ],
        "priceRange": "KES"
      },
      {
        "@type": "WebSite",
        "@id": "https://www.yourwebsite.com/#website", // IMPORTANT: Replace with your domain
        "url": "https://www.yourwebsite.com/", // IMPORTANT: Replace with your domain
        "name": "Luxe & Allure Events",
        "publisher": {
          "@id": "https://www.yourwebsite.com/#organization"
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://www.yourwebsite.com/search?q={search_term_string}", // Optional: if you have a search feature
          "query-input": "required name=search_term_string"
        }
      }
    ]
  };


  return (
    <div className="min-h-screen bg-background">
      {/* ---包括的なSEOのためのHelmet実装 --- */}
      <Helmet>
        {/* --- Primary Meta Tags --- */}
        <title>Top Event Decor & Wedding Planners in Nairobi, Kenya | Luxe & Allure</title>
        <meta name="description" content="Discover Luxe & Allure, the leading event decor and wedding planning company in Nairobi, Kenya. We create unforgettable, luxury events with bespoke styling and flawless execution. Contact us for a free quote!" />
        <meta name="keywords" content="event decor Nairobi, wedding planners Kenya, top event company Kenya, luxury wedding decor Nairobi, corporate event management, party planners Kenya, bespoke event styling, decor companies in Nairobi" />
        <link rel="canonical" href="https://www.yourwebsite.com/" /> {/* IMPORTANT: Replace with your domain */}

        {/* --- Open Graph / Facebook Meta Tags --- */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.yourwebsite.com/" /> {/* IMPORTANT: Replace with your domain */}
        <meta property="og:title" content="Top Event Decor & Wedding Planners in Nairobi, Kenya | Luxe & Allure" />
        <meta property="og:description" content="Creating unforgettable, luxury events with bespoke styling and flawless execution." />
        <meta property="og:image" content="https://www.yourwebsite.com/assets/social-preview.jpg" /> {/* IMPORTANT: Create a 1200x630px social preview image */}

        {/* --- Twitter Meta Tags --- */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://www.yourwebsite.com/" /> {/* IMPORTANT: Replace with your domain */}
        <meta property="twitter:title" content="Top Event Decor & Wedding Planners in Nairobi, Kenya | Luxe & Allure" />
        <meta property="twitter:description" content="Creating unforgettable, luxury events with bespoke styling and flawless execution." />
        <meta property="twitter:image" content="https://www.yourwebsite.com/assets/social-preview.jpg" /> {/* IMPORTANT: Use the same social preview image */}
        
        {/* --- Structured Data Injection --- */}
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      </Helmet>

      <Header />
      <ContactWidget />
      
      <main>
        {/* The Breadcrumb on the homepage root is often omitted, but can be kept if desired */}
        {/* <div className="container mx-auto px-6 lg:px-8 pt-6">
          <Breadcrumb />
        </div> */}
        
        <HeroSection />
        <ServicesOverview />
        <CompanyIntroduction />
        <RelevantTopics />
        <TestimonialsCarousel />
        <NewsletterSubscription />
      </main>

      {/* --- SEO ENHANCED FOOTER --- */}
      <footer className="bg-secondary text-secondary-foreground py-12">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <img
                  src="/assets/logoo.png"
                  alt="Luxe & Allure Events Company Logo"
                  className="h-10 w-auto"
                />
                <div>
                  <h3 className="font-heading font-bold text-lg">Luxe & Allure Events</h3>
                  <p className="font-caption text-xs text-secondary-foreground/70">Events Company</p>
                </div>
              </div>
              <p className="font-body text-sm text-secondary-foreground/80 leading-relaxed">
                Nairobi's premier choice for creating unforgettable wedding moments and premium event experiences with meticulous attention to detail.
              </p>
              <div className="space-y-2">
                <a href="tel:0727937010" className="flex items-center space-x-2 text-sm hover:text-accent transition-smooth">
                  <span aria-hidden="true">📞</span>
                  <span>0727 937 010</span>
                </a>
                <a href="tel:0721320787" className="flex items-center space-x-2 text-sm hover:text-accent transition-smooth">
                  <span aria-hidden="true">📞</span>
                  <span>0721 320 787</span>
                </a>
              </div>
            </div>

            {/* --- NEW: QUICK LINKS FOR SEO & UX --- */}
            <div className="space-y-4">
              <h4 className="font-heading font-bold text-lg">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="/" className="font-body text-sm text-secondary-foreground/80 hover:text-accent transition-smooth">Home</a></li>
                <li><a href="/services" className="font-body text-sm text-secondary-foreground/80 hover:text-accent transition-smooth">Our Services</a></li>
                <li><a href="/gallery" className="font-body text-sm text-secondary-foreground/80 hover:text-accent transition-smooth">Gallery</a></li>
                <li><a href="/about" className="font-body text-sm text-secondary-foreground/80 hover:text-accent transition-smooth">About Us</a></li>
                <li><a href="/contact" className="font-body text-sm text-secondary-foreground/80 hover:text-accent transition-smooth">Contact</a></li>
              </ul>
            </div>

            {/* Social Media */}
            <div className="space-y-4">
              <h4 className="font-heading font-bold text-lg">Follow Us</h4>
              <div className="flex space-x-3">
                {[
                  { name: 'Facebook', url: 'https://www.facebook.com/luxeallureevents/' },
                  { name: 'Instagram', url: 'https://www.instagram.com/luxeallureevents/' },
                  { name: 'TikTok', url: 'https://www.tiktok.com/discover/luxe-allure-events?lang=en' },
                  { name: 'WhatsApp', url: 'https://wa.me/254727937010' }
                ].map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Follow us on ${social.name}`}
                    className="w-10 h-10 bg-secondary-foreground/10 rounded-lg flex items-center justify-center hover:bg-accent hover:text-accent-foreground transition-smooth"
                  >
                    <span className="sr-only">{social.name}</span>
                    <span className="text-sm font-bold">{social.name.charAt(0)}</span>
                  </a>
                ))}
              </div>
              <p className="font-body text-sm text-secondary-foreground/80">
                Stay connected for event inspiration and company updates.
              </p>
            </div>
            
            {/* Empty div for layout spacing on large screens */}
            <div className="hidden lg:block"></div>

          </div>

          {/* Bottom Bar */}
          <div className="border-t border-secondary-foreground/20 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between">
            <p className="font-body text-sm text-secondary-foreground/70">
              © {new Date().getFullYear()} Luxe & Allure Events Company. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <a href="/privacy-policy" className="font-body text-sm text-secondary-foreground/70 hover:text-accent transition-smooth">
                Privacy Policy
              </a>
              <a href="/terms-of-service" className="font-body text-sm text-secondary-foreground/70 hover:text-accent transition-smooth">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;